import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    getSubscriptionPlans, 
    createSubscription, 
    getBoundCreditCards, 
    getUserSubscription,
    updateSubscription,
    cancelSubscription,
    resumeSubscription
} from '../services/api';
import { getAccessPassStatus } from '../services/api';
import SubscriptionPlan from '../components/SubscriptionPlan';

const SubscriptionPage = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [userSubscription, setUserSubscription] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [accessPassStatus, setAccessPassStatus] = useState(null);
    const navigate = useNavigate();

    const fetchSubscriptionData = useCallback(async () => {
        try {
            // 在重新獲取資料時，短暫顯示載入狀態，提供視覺回饋
            setLoading(true);
            const plansResponse = await getSubscriptionPlans();
            setPlans(plansResponse.data.data);

            const subResponse = await getUserSubscription();
            setUserSubscription(subResponse.data.data.length > 0 ? subResponse.data.data[0] : null);

            try {
                const passResp = await getAccessPassStatus();
                setAccessPassStatus(passResp.data.data);
            } catch (_) {
                setAccessPassStatus(null);
            }
        } catch (err) {
            setError('無法獲取訂閱資料，請稍後再試。');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSubscriptionData();
    }, [fetchSubscriptionData]);
    
    // 【修改處】調整 handlePlanChange 的邏輯
    const handlePlanChange = async (plan) => {
        if (submitting) return;
        setSubmitting(true);

        const action = userSubscription ? updateSubscription : createSubscription;
        const actionName = userSubscription ? '更換' : '訂閱';

        try {
            // 檢查付費方案是否已綁卡（以後端傳回的折扣後含稅價為準）
            const gross = typeof plan.priceGross === 'number' ? plan.priceGross : plan.price * (1 + (plan.vat || 0));
            const discounted = typeof plan.discountedPriceGross === 'number' ? plan.discountedPriceGross : gross;
            if (discounted > 0) {
                const cardsResponse = await getBoundCreditCards();
                if (cardsResponse.data.data.length === 0) {
                    alert('您需要先綁定一張信用卡才能訂閱付費方案。');
                    navigate('/bind-card');
                    setSubmitting(false);
                    return;
                }
            }
            
            const response = await action(plan.subscriptionPlanId);
            
            // 根據 API 回應處理需要付款的流程
            if (response.data.data?.action === 'pending payment authorization') {
                // 修改提示文字，告知使用者請求已在背景處理
                alert(`已送出付款授權請求。您的訂閱狀態將在幾秒後自動更新，請稍候。`);
                
                // 移除頁面跳轉
                // navigate('/payment-status?status=pending');

                // 在 5 秒後自動重新獲取一次訂閱資料，以更新畫面狀態
                setTimeout(() => {
                    fetchSubscriptionData();
                }, 5000); 

            } else {
                // 對於不需要額外授權的變更（如訂閱免費方案、降級），立即刷新
                alert(`方案${actionName}成功！`);
                await fetchSubscriptionData();
            }

        } catch (err) {
            alert(`方案${actionName}失敗：${err.response?.data?.message || '發生未知錯誤'}`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = async () => {
        if (submitting) return;
        if (!window.confirm('您確定要取消目前的訂閱嗎？您的權益將保留至本期結束。')) return;

        setSubmitting(true);
        try {
            await cancelSubscription();
            alert('訂閱已成功取消。');
            await fetchSubscriptionData();
        } catch (err) {
            alert(`取消訂閱失敗：${err.response?.data?.message || '發生未知錯誤'}`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleResume = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await resumeSubscription();
            alert('訂閱已成功恢復！');
            await fetchSubscriptionData();
        } catch (err) {
            alert(`恢復訂閱失敗：${err.response?.data?.message || '發生未知錯誤'}`);
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleString() : 'N/A';
    
    if (loading) return <p>正在載入方案...</p>;
    if (error) return <p className="error-message">{error}</p>;

    return (
        <div className="page-container">
            <h2>選擇最適合您的方案</h2>
            {accessPassStatus?.active && (
                <div className="notice-banner" style={{marginBottom: '1rem'}}>
                    通行證有效中，可免費切換指定方案，效期至：{new Date(accessPassStatus.expiresAt).toLocaleString()}
                </div>
            )}
            
            {userSubscription && (
                <div className="current-subscription-panel">
                    <div className="panel-header">
                        <h3>目前的訂閱資訊</h3>
                        <div className="subscription-actions">
                            {userSubscription.cancelledAt ? (
                                <button onClick={handleResume} disabled={submitting} className="action-button resume">恢復訂閱</button>
                            ) : (
                                <button onClick={handleCancel} disabled={submitting} className="action-button cancel">取消訂閱</button>
                            )}
                        </div>
                    </div>
                    <ul className="subscription-details-list">
                        <li><strong>方案名稱:</strong> {userSubscription.planName}</li>
                        <li><strong>訂閱狀態:</strong> <span className={`status-badge status-${userSubscription.status}`}>{userSubscription.status}</span></li>
                        <li><strong>下一期方案:</strong> {userSubscription.pendingPlanName || '無變更'}</li>
                        <li><strong>價格:</strong> {userSubscription.currency} ${userSubscription.price} (VAT: {userSubscription.vat * 100}%)</li>
                        <li><strong>訂閱 ID:</strong> {userSubscription.userSubscriptionId}</li>
                        <li><strong>建立時間:</strong> {formatDate(userSubscription.createdAt)}</li>
                        <li><strong>本期開始時間:</strong> {formatDate(userSubscription.startDate)}</li>
                        <li><strong>本期結束時間:</strong> {formatDate(userSubscription.endDate)}</li>
                        <li><strong>下次扣款時間:</strong> {formatDate(userSubscription.nextBillingDate)}</li>
                        <li><strong>取消時間:</strong> {formatDate(userSubscription.cancelledAt)}</li>
                        <li><strong>是否支援儲值:</strong> {userSubscription.deposit ? '是' : '否'}</li>
                    </ul>
                </div>
            )}

            <div className="plans-container">
                {plans.map(plan => (
                    <SubscriptionPlan 
                        key={plan.subscriptionPlanId} 
                        plan={plan} 
                        userSubscription={userSubscription}
                        isSubmitting={submitting}
                        onPlanChange={handlePlanChange}
                    />
                ))}
            </div>
        </div>
    );
};

export default SubscriptionPage;